<template>
  <aside class="panel" :class="{ 'panel-compact': isCompact }">
    <header class="head">
      <span class="title">Téléphone</span>
      <span
        class="sip-state"
        :data-state="sipState"
        :title="sipStateTitle"
        :aria-label="`Connexion SIP : ${sipStateLabel}`"
      >
        <span class="sip-dot"></span>
        <span class="sip-label">{{ sipStateLabel }}</span>
      </span>
      <span class="state" :data-status="callStore.callStatus">
        <span class="state-dot"></span>
        {{ statusLabel }}
      </span>
      <!-- Appel parti par le SIP par défaut (ligne de secours) : la ligne
           normale de l'agent est en panne. Effacé à la fin de l'appel. -->
      <span
        v-if="fallbackLine"
        class="state"
        data-status="fallback"
        :title="`Appel via la ligne de secours « ${fallbackLine} »`"
        :aria-label="`Ligne de secours : ${fallbackLine}`"
      >
        <Icon name="alert-triangle" :size="11" /> secours
      </span>
      <!-- Bouton détacher (mini) / réintégrer (plein) : redimensionne la
           fenêtre Electron et place en always-on-top en mode mini. -->
      <button
        type="button"
        class="compact-toggle"
        :title="isCompact ? 'Agrandir la fenêtre' : 'Mini softphone (always-on-top)'"
        :aria-label="isCompact ? 'Agrandir la fenêtre' : 'Passer en mini softphone'"
        @click="toggleCompact"
      >
        <Icon :name="isCompact ? 'maximize-2' : 'minimize-2'" :size="14" />
      </button>
      <!-- Replie tout le panneau téléphone (la connexion SIP reste active). -->
      <button
        type="button"
        class="compact-toggle"
        title="Replier le téléphone"
        aria-label="Replier le panneau téléphone"
        @click="emit('toggle-collapse')"
      >
        <Icon name="chevrons-right" :size="14" />
      </button>
    </header>

    <!-- En mode compact, on embarque le sélecteur de statut ici (sinon il
         est dans la topbar masquée). -->
    <div v-if="isCompact" class="compact-status">
      <StatusSelector />
    </div>

    <!-- Composeur — masqué pendant la clôture : la fiche de fin d'appel
         prend sa place (l'espace de travail reste ouvert à gauche). -->
    <div v-if="!callStore.showDisposition" class="display">
      <div v-if="callStore.selectedEntry || callStore.callStatus !== 'idle'" class="contact">
        <span class="contact-avatar">{{ initial(callStore.selectedEntry?.name || contactLookup?.identified?.name) }}</span>
        <span class="contact-name">{{ callStore.selectedEntry?.name || contactLookup?.identified?.name || 'Inconnu' }}</span>
      </div>

      <div v-if="!callStore.isActive" class="number-row">
        <select v-model="countryCode" class="country" :title="`Indicatif (${currentCountry.label})`">
          <option v-for="c in COUNTRIES" :key="c.label" :value="c.code">
            {{ c.flag }} {{ c.code || 'Interne' }}
          </option>
        </select>
        <input
          ref="numberInput"
          class="number-input"
          :value="callStore.dialNumber"
          @input="onNumberInput"
          @keydown.enter="startCall"
          placeholder="Numéro à composer"
          inputmode="tel"
          autocomplete="off"
        />
      </div>
      <div v-else class="number">{{ callStore.dialNumber }}</div>

      <!-- Bannière CLIENT proéminente : visible dès la composition (même
           manuelle), avec toutes les campagnes dont le numéro est client. -->
      <div v-if="isClientNumber" class="client-banner">
        <ClientBadge :size="18" :title="''" />
        <div class="cb-txt">
          <span class="cb-title">Numéro client</span>
          <span v-if="clientCampaigns.length" class="cb-camps">{{ clientCampaigns.map((c) => c.name).join(' · ') }}</span>
        </div>
      </div>

      <!-- Suggestions de numéros pendant la composition (dès le 8e chiffre) :
           chaque ligne montre nom + dernière disposition + tentatives. -->
      <ul v-if="showSuggestions && !callStore.isActive" class="suggest-list">
        <li
          v-for="s in suggestions"
          :key="s.id"
          class="suggest-item"
          @mousedown.prevent="pickSuggestion(s)"
        >
          <div class="sg-main">
            <span class="sg-phone mono">{{ s.phone }}</span>
            <span v-if="s.name" class="sg-name">{{ s.name }}</span>
            <span v-if="s.campaign_name" class="sg-camp">{{ s.campaign_name }}</span>
          </div>
          <div class="sg-meta">
            <span v-if="s.last_disposition" class="sg-dispo">
              <span class="sg-dot" :style="{ background: s.last_disposition_color || 'var(--text-muted)' }"></span>
              {{ s.last_disposition }}
            </span>
            <span class="sg-att">{{ s.call_attempts }} tent.</span>
          </div>
        </li>
      </ul>

      <div v-if="callStore.callStatus === 'in_call'" class="timer">{{ duration }}</div>
      <div v-else-if="callStore.callStatus === 'dialing' || callStore.callStatus === 'ringing'" class="timer">
        Connexion...
      </div>
    </div>

    <!-- Contact card enrichie : affichée pendant un appel/sonnerie.
         Mode `restricted` pour les agents — ils voient nom/téléphone/service
         précédent/dernier résultat/notes, mais PAS l'adresse, l'URL source
         PagesJaunes, les emails alternatifs, ou les champs CSV extras.
         Owner/admin/team_admin voient tout. -->
    <ContactCard
      v-if="showContactCard"
      :lookup="contactLookup"
      :phone="callStore.dialNumber"
      :fallback-name="callStore.selectedEntry?.name || ''"
      :loading="lookupLoading"
      :restricted="!auth.isTeamAdmin"
      compact
      class="panel-contact-card"
      @dial="dialAnother"
    />

    <!-- Outils agent : script, note d'appel, bloc-notes, transcription -->
    <div v-if="callStore.selectedEntry || callStore.isActive" class="agent-tools">
      <button class="tool-btn" @click="scriptOpen = true" :disabled="!callStore.selectedEntry">
        <Icon name="book-open" :size="13" /><span>Script</span>
      </button>
      <button class="tool-btn" :class="{ active: showNote }" @click="showNote = !showNote">
        <Icon name="edit-2" :size="13" /><span>Note</span>
      </button>
      <button class="tool-btn" @click="notebookOpen = true">
        <Icon name="file-text" :size="13" /><span>Bloc-notes</span>
      </button>
      <button
        class="tool-btn tool-btn-transcription"
        :class="{ active: transcription.status.value === 'active' }"
        :disabled="!canTranscribe"
        :title="transcriptionTitle"
        @click="toggleTranscription"
      >
        <Icon name="mic-off" :size="13" v-if="transcription.status.value === 'active'" />
        <Icon name="type" :size="13" v-else />
        <span>{{ transcription.status.value === 'active' ? 'Arrêter' : 'Transcrire' }}</span>
      </button>
    </div>
    <textarea
      v-if="showNote && (callStore.selectedEntry || callStore.isActive)"
      v-model="callStore.callNote"
      class="call-note"
      rows="3"
      placeholder="Note pour cet appel — reprise automatiquement dans la fiche de fin d'appel…"
    />
    <!-- Panneau de transcription live -->
    <div
      v-if="transcription.status.value === 'active' || transcription.status.value === 'checking' || transcription.status.value === 'connecting'"
      class="transcription-panel"
    >
      <div class="transcription-header">
        <span class="transcription-dot" />
        <span class="transcription-label">Transcription en cours…</span>
      </div>
      <div class="transcription-body" ref="transcriptionScroll">
        <p v-if="transcription.lines.value.length === 0" class="transcription-empty">
          En attente de la parole…
        </p>
        <p
          v-for="(line, i) in transcription.lines.value"
          :key="i"
          class="transcription-line"
          :class="{ interim: !line.isFinal }"
        >{{ line.text }}</p>
      </div>
    </div>
    <div v-if="transcription.status.value === 'denied'" class="transcription-denied">
      <Icon name="alert-circle" :size="13" />
      <span>{{ transcription.deniedReason.value || 'Transcription non disponible pour cette campagne.' }}</span>
    </div>

    <ScriptModal v-model="scriptOpen" :entry="callStore.selectedEntry" />
    <NotebookModal v-model="notebookOpen" />
    <TransferModal v-model="transferOpen" @transfer="doTransfer" />

    <!-- Notification flottante pour appel entrant — overlay global -->
    <IncomingCallToast
      :visible="isIncomingRinging"
      :phone="callStore.dialNumber"
      :lookup="contactLookup"
      :fallback-name="callStore.selectedEntry?.name || ''"
      :restricted="!auth.isTeamAdmin"
      @answer="answer"
      @decline="hangup"
    />

    <!-- Fin d'appel : la fiche de clôture remplace le composeur. -->
    <DispositionModal v-if="callStore.showDisposition" />

    <!-- Dialpad -->
    <div v-else-if="!callStore.isActive" class="pad">
      <button v-for="k in keys" :key="k" class="key" @click="press(k)">
        {{ k }}
      </button>
      <button class="key key-act" @click="backspace" :disabled="!callStore.dialNumber">
        <Icon name="backspace" :size="18" />
      </button>
      <button class="key key-call" @click="startCall" :disabled="!callStore.canDial || !canCall" :title="callTitle">
        <Icon name="phone" :size="20" />
      </button>
      <button class="key key-act" @click="callStore.setDial('')" :disabled="!callStore.dialNumber">
        <Icon name="x" :size="18" />
      </button>
    </div>

    <!-- Réglages audio HORS APPEL : jusqu'ici ces curseurs n'existaient que
         pendant une conversation, l'agent devait donc attendre d'être en ligne
         pour corriger son volume. Repliés par défaut. -->
    <div v-if="!callStore.isActive && !callStore.showDisposition" class="mic-settings">
      <button
        type="button"
        class="mic-settings-toggle"
        :class="{ open: idleAudioOpen }"
        @click="idleAudioOpen = !idleAudioOpen"
      >
        <Icon name="volume-2" :size="13" class="mic-vol-ico" />
        <span class="mic-st-label">Audio</span>
        <span class="mic-st-summary">Écoute {{ speakerVolume }}% · Micro {{ micVolume }}% · Sonnerie {{ ringVolume }}%</span>
        <Icon name="chevron-down" :size="15" class="mic-st-chev" />
      </button>
      <div v-show="idleAudioOpen" class="mic-settings-body">
        <div class="mic-vol" :title="`Volume du correspondant : ${speakerVolume}%`">
          <Icon name="volume-2" :size="14" class="mic-vol-ico" />
          <input
            type="range" min="0" max="200" step="5"
            :value="speakerVolume" @input="onSpeakerVolume"
            class="mic-range" aria-label="Volume du correspondant"
          />
          <span class="mic-vol-pct">{{ speakerVolume }}%</span>
        </div>
        <div class="mic-vol" :title="`Volume de votre micro : ${micVolume}%`">
          <Icon :name="micVolume === 0 ? 'mic-off' : 'mic'" :size="14" class="mic-vol-ico" />
          <input
            type="range" min="0" max="200" step="5"
            :value="micVolume" @input="onMicVolume"
            class="mic-range" aria-label="Volume du micro"
          />
          <span class="mic-vol-pct">{{ micVolume }}%</span>
        </div>
        <!-- Sonnerie : on la fait entendre pendant le réglage, sinon l'agent
             règle à l'aveugle. Elle s'arrête dès qu'il lâche le curseur. -->
        <div class="mic-vol" :title="`Volume de la sonnerie : ${ringVolume}%`">
          <Icon name="phone-incoming" :size="14" class="mic-vol-ico" />
          <input
            type="range" min="0" max="100" step="5"
            :value="ringVolume" @input="onRingVolume" @change="stopRingPreview"
            class="mic-range" aria-label="Volume de la sonnerie"
          />
          <span class="mic-vol-pct">{{ ringVolume }}%</span>
        </div>
        <p class="idle-audio-hint">
          <Icon name="info" :size="11" />
          Réglable à tout moment, même hors appel. Ces réglages sont les vôtres.
        </p>
      </div>
    </div>

    <!-- Active call controls -->
    <div v-else class="controls">
      <div v-if="isIncomingRinging" class="incoming-row">
        <button class="answer" @click="answer">
          <Icon name="phone" :size="18" />
          <span>Décrocher</span>
        </button>
        <button class="hangup" @click="hangup">
          <Icon name="phone-off" :size="18" />
          <span>Refuser</span>
        </button>
      </div>
      <template v-else>
        <div class="ctrl-row">
          <button class="ctrl" :class="{ active: callStore.muted }" @click="toggleMute">
            <Icon :name="callStore.muted ? 'mic-off' : 'mic'" :size="18" />
            <span>{{ callStore.muted ? 'Muet' : 'Mute' }}</span>
          </button>
          <button class="ctrl" :class="{ active: callStore.onHold }" @click="toggleHold">
            <Icon name="pause" :size="18" />
            <span>{{ callStore.onHold ? 'Reprendre' : 'Pause' }}</span>
          </button>
          <button class="ctrl" @click="askTransfer">
            <Icon name="forward" :size="18" />
            <span>Transfert</span>
          </button>
          <button class="ctrl" :class="{ active: showDtmf }" @click="showDtmf = !showDtmf">
            <Icon name="hash" :size="18" />
            <span>Pavé</span>
          </button>
        </div>
        <!-- Réglages micro repliables (volume + anti-bruit + filtre) — fermés
             par défaut pour ne pas surcharger le panneau d'appel. Résumé visible. -->
        <div class="mic-settings">
          <button type="button" class="mic-settings-toggle" :class="{ open: micPanelOpen }" @click="micPanelOpen = !micPanelOpen">
            <Icon name="mic" :size="13" class="mic-vol-ico" />
            <span class="mic-st-label">Micro</span>
            <span class="mic-st-summary">{{ DENOISE_LABELS[micDenoise] }} · {{ micVolume }}%</span>
            <Icon name="chevron-down" :size="15" class="mic-st-chev" />
          </button>
          <div v-show="micPanelOpen" class="mic-settings-body">
        <!-- Volume du micro de l'agent : agit en direct sur la voix transmise. -->
        <div class="mic-vol" :title="`Volume de votre micro : ${micVolume}%`">
          <Icon :name="micVolume === 0 ? 'mic-off' : 'mic'" :size="14" class="mic-vol-ico" />
          <input
            type="range" min="0" max="200" step="5"
            :value="micVolume" @input="onMicVolume"
            class="mic-range" aria-label="Volume du micro"
          />
          <span class="mic-vol-pct">{{ micVolume }}%</span>
        </div>
        <!-- Anti-bruit ↔ Clarté EN APPEL : dose le filtrage en direct. -->
        <div class="mic-gate">
          <div class="mic-gate-head">
            <Icon name="mic" :size="13" class="mic-vol-ico" />
            <span class="mic-gate-title">Anti-bruit</span>
            <span class="mic-gate-state">{{ DENOISE_LABELS[micDenoise] }}</span>
          </div>
          <div class="mic-gate-row">
            <span class="mic-gate-cap">Clarté</span>
            <input
              type="range" min="0" :max="DENOISE_MAX" step="1"
              :value="micDenoise" @input="onMicDenoise" @change="persistMicSettings"
              class="mic-range" aria-label="Niveau d'anti-bruit" :disabled="!canEditMicSettings"
            />
            <span class="mic-gate-cap">Anti-bruit</span>
          </div>
        </div>
        <!-- Filtre voix proche EN APPEL : vumètre live (vert = votre voix passe)
             + réglage du seuil en direct. -->
        <div class="mic-gate">
          <div class="mic-gate-head">
            <Icon name="mic" :size="13" class="mic-vol-ico" />
            <span class="mic-gate-title">Filtre voix proche</span>
            <span class="mic-gate-state" :class="{ open: gateOpen }">{{ gateOpen ? 'Vous passez' : 'Silence' }}</span>
          </div>
          <div class="gate-meter" :title="`Seuil : ${micGateDb} dBFS`">
            <div class="gate-meter-fill" :class="{ open: gateOpen }" :style="{ width: micLevelPct + '%' }" />
            <div class="gate-meter-thresh" :style="{ left: gateThreshPct + '%' }" />
          </div>
          <div class="mic-gate-row">
            <span class="mic-gate-cap">Permissif</span>
            <input
              type="range" :min="GATE_DB_MIN" :max="GATE_DB_MAX" step="1"
              :value="micGateDb" @input="onMicGateDb" @change="persistMicSettings"
              class="mic-range" aria-label="Seuil du filtre micro" :disabled="!canEditMicSettings"
            />
            <span class="mic-gate-cap">Strict</span>
          </div>
        </div>
        <!-- Haut-parleur : voix du CORRESPONDANT. Volume (avec boost jusqu'à
             200 %) + « clarté » (passe-haut + compresseur léger). Local à
             l'agent (sa propre écoute), appliqué en direct. -->
        <div class="mic-vol" :title="`Volume du correspondant : ${speakerVolume}%`">
          <Icon name="volume-2" :size="14" class="mic-vol-ico" />
          <input
            type="range" min="0" max="200" step="5"
            :value="speakerVolume" @input="onSpeakerVolume"
            class="mic-range" aria-label="Volume du correspondant"
          />
          <span class="mic-vol-pct">{{ speakerVolume }}%</span>
        </div>
        <div class="mic-gate">
          <div class="mic-gate-head">
            <Icon name="volume-2" :size="13" class="mic-vol-ico" />
            <span class="mic-gate-title">Clarté correspondant</span>
            <button
              type="button"
              class="clarity-toggle"
              :class="{ on: clarityOn }"
              @click="onToggleClarity"
            >{{ clarityOn ? 'Activée' : 'Coupée' }}</button>
          </div>
        </div>
          </div>
        </div>
        <div
          v-if="showDtmf"
          ref="dtmfPad"
          class="dtmf"
          tabindex="0"
        >
          <div class="dtmf-readout">
            <span v-if="dtmfSent" class="dtmf-sent">{{ dtmfSent }}</span>
            <span v-else class="dtmf-ph">Tapez au clavier ou cliquez</span>
          </div>
          <div class="dtmf-grid">
            <button v-for="k in keys" :key="k" class="dtmf-key" @click="sendDtmf(k)">{{ k }}</button>
          </div>
          <p class="dtmf-hint">
            <Icon name="info" :size="11" />
            Pavé ouvert : tapez <b>0-9</b>, <b>*</b>, <b>#</b> au clavier (où que vous soyez) — ou cliquez.
          </p>
        </div>
        <button class="hangup" @click="hangup">
          <Icon name="phone-off" :size="18" />
          <span>Raccrocher</span>
        </button>
      </template>
    </div>

    <div v-if="!canCall && !callStore.isActive && !callStore.showDisposition" class="warn">
      <Icon name="alert-triangle" :size="14" />
      <span>Passer en <b>Disponible</b> pour appeler</span>
    </div>

    <!-- Réglages micro repliables — au repos. Fermé par défaut (panneau épuré) ;
         résumé visible, on ouvre pour régler. -->
    <div v-if="!callStore.isActive && !callStore.showDisposition" class="mic-settings">
      <button type="button" class="mic-settings-toggle" :class="{ open: micPanelOpen }" @click="micPanelOpen = !micPanelOpen">
        <Icon name="mic" :size="13" class="mic-vol-ico" />
        <span class="mic-st-label">Micro</span>
        <span class="mic-st-summary">{{ DENOISE_LABELS[micDenoise] }}</span>
        <Icon name="chevron-down" :size="15" class="mic-st-chev" />
      </button>
      <div v-show="micPanelOpen" class="mic-settings-body">
        <!-- Anti-bruit ↔ Clarté -->
        <div class="mic-gate">
          <div class="mic-gate-head">
            <Icon name="mic" :size="13" class="mic-vol-ico" />
            <span class="mic-gate-title">Anti-bruit</span>
            <span class="mic-gate-state">{{ DENOISE_LABELS[micDenoise] }}</span>
          </div>
          <div class="mic-gate-row">
            <span class="mic-gate-cap">Clarté</span>
            <input
              type="range" min="0" :max="DENOISE_MAX" step="1"
              :value="micDenoise" @input="onMicDenoise" @change="persistMicSettings"
              class="mic-range" aria-label="Niveau d'anti-bruit" :disabled="!canEditMicSettings"
            />
            <span class="mic-gate-cap">Anti-bruit</span>
          </div>
        </div>

        <!-- Filtre voix proche (noise gate « proximité ») -->
        <div class="mic-gate">
      <div class="mic-gate-head">
        <Icon name="mic" :size="13" class="mic-vol-ico" />
        <span class="mic-gate-title">Filtre voix proche</span>
        <button v-if="canEditMicSettings" class="mic-gate-test" :class="{ active: micTesting }" @click="toggleMicTest">
          {{ micTesting ? 'Arrêter' : 'Tester' }}
        </button>
      </div>
      <div class="gate-meter" :title="`Seuil : ${micGateDb} dBFS`">
        <div class="gate-meter-fill" :class="{ open: gateOpen }" :style="{ width: micLevelPct + '%' }" />
        <div class="gate-meter-thresh" :style="{ left: gateThreshPct + '%' }" />
      </div>
      <div class="mic-gate-row">
        <span class="mic-gate-cap">Permissif</span>
        <input
          type="range" :min="GATE_DB_MIN" :max="GATE_DB_MAX" step="1"
          :value="micGateDb" @input="onMicGateDb" @change="persistMicSettings"
          class="mic-range" aria-label="Seuil du filtre micro" :disabled="!canEditMicSettings"
        />
        <span class="mic-gate-cap">Strict</span>
      </div>
      <p v-if="canEditMicSettings" class="mic-gate-hint">
        <b>Tester</b>, puis parlez : la barre doit être <b>verte</b>. Faites parler
        quelqu'un à côté : sa barre doit rester sous le repère. Vers <b>Strict</b>
        si le voisin passe, vers <b>Permissif</b> si votre voix est coupée.
      </p>
      <p v-else class="mic-gate-hint">
        Réglages micro gérés par l'administrateur (lecture seule).
      </p>
        </div>
      </div>
    </div>
  </aside>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted, watch, nextTick } from 'vue'
import { useActiveCallStore } from '@/shared/stores/active_call'
import { useAgentStatusStore } from '@/shared/stores/agent_status'
import { useAuthStore } from '@/shared/stores/auth'
import { QueueAPI, DispositionAPI, type CampaignDisposition, type ContactLookup, type DialCheck, type QueueSuggestion } from '@/shared/api/queue'
import { MicAPI } from '@/shared/api/system'
import { TelephonyAPI } from '@/modules/telephony/api/telephony_api'
import { useDialerSessionStore } from '@/shared/stores/dialer_session'
import { causeToSystemDisposition } from '../utils/auto_disposition'
import Icon from '@/shared/components/Icon.vue'
import ContactCard from '@/shared/components/ContactCard.vue'
import IncomingCallToast from './IncomingCallToast.vue'
import ScriptModal from './ScriptModal.vue'
import NotebookModal from './NotebookModal.vue'
import DispositionModal from './DispositionModal.vue'
import TransferModal from './TransferModal.vue'
import { SipController } from '../controllers/sip_controller'
import { Ringtone, loadRingVolume, saveRingVolume } from '@/shared/audio/ringtone'
import { loadOutputDeviceId } from '@/shared/audio/audio_devices'
import { useKeyboardShortcuts } from '@/shared/composables/useKeyboardShortcuts'
import { useToasts } from '@/shared/stores/toasts'
import { useConfirmStore } from '@/shared/stores/confirm'
import ClientBadge from '@/shared/components/ClientBadge.vue'
import { useCompactMode } from '@/shared/composables/useCompactMode'
import { useUserActivity } from '@/shared/composables/useUserActivity'
import StatusSelector from '@/modules/agent-status/views/StatusSelector.vue'
import { useTranscription } from '../composables/useTranscription'

const { isCompact } = useCompactMode()
const userActivity = useUserActivity()

// Bridge Electron : redimensionne la fenêtre en mini (380×640 + always-on-top)
// ou la restaure (rect par défaut). En dev web pur (sans Electron), on
// retombe sur un toggle CSS uniquement via `window.resizeTo` si dispo.
async function toggleCompact() {
  const api = (window as any).pexitalk?.app
  const next = !isCompact.value
  if (api?.setCompact) {
    try { await api.setCompact(next) } catch { /* ignore */ }
  } else if (typeof window.resizeTo === 'function') {
    if (next) window.resizeTo(380, 640)
    else window.resizeTo(1280, 800)
  }
}

const toasts = useToasts()
const confirm = useConfirmStore()

// Replier tout le panneau : géré par le parent (App.vue) qui possède la
// préférence persistée et le rail de réouverture.
const emit = defineEmits<{ (e: 'toggle-collapse'): void }>()

const callStore = useActiveCallStore()
const agentStore = useAgentStatusStore()
const auth = useAuthStore()
// Réglages micro (Anti-bruit + Filtre voix proche) : modifiables par le OWNER
// seulement. Les autres rôles les VOIENT mais en lecture seule (sliders
// désactivés, pas de bouton Tester).
const canEditMicSettings = computed(() => auth.isOwner)
const dialer = useDialerSessionStore()
const now = ref(Date.now())

// Cache des dispositions par campagne (auto-classement des appels non décrochés).
const _dispoCache: Record<number, CampaignDisposition[]> = {}
async function _campaignDispositions(campaignId: number): Promise<CampaignDisposition[]> {
  if (!_dispoCache[campaignId]) {
    try { _dispoCache[campaignId] = await DispositionAPI.list(campaignId) }
    catch { return [] }
  }
  return _dispoCache[campaignId]
}

/** Classe automatiquement un appel NON décroché (occupé / pas de réponse /
 *  injoignable) avec la disposition système correspondante, SANS modale, puis
 *  enchaîne. Les appels DÉCROCHÉS restent en statuage manuel (« Terminer »).
 *  Renvoie `true` si classé + enchaîné ; `false` → on retombe sur la modale. */
async function tryAutoClassify(cause?: string): Promise<boolean> {
  const entry = callStore.selectedEntry
  if (!entry || !entry.campaign) return false
  const title = causeToSystemDisposition(cause)
  if (!title) return false
  const dispo = (await _campaignDispositions(entry.campaign))
    .find((d) => d.is_system && d.title === title)
  if (!dispo) return false
  try {
    await QueueAPI.complete(entry.id, { disposition_id: dispo.id })
  } catch {
    return false
  }
  toasts.info('Appel classé automatiquement', title)
  callStore.reset()        // pas de modale manuelle
  dialer.onCallCompleted() // enchaîne sur le numéro suivant
  return true
}

// === Indicatifs pays ===
const COUNTRIES = [
  { code: '+1',   flag: '🇨🇦', label: 'Canada (par défaut)' },
  { code: '+1',   flag: '🇺🇸', label: 'États-Unis' },
  { code: '',     flag: '🔢', label: 'Sans indicatif (interne / numéro déjà complet)' },
  { code: '+33',  flag: '🇫🇷', label: 'France' },
  { code: '+212', flag: '🇲🇦', label: 'Maroc' },
  { code: '+44',  flag: '🇬🇧', label: 'Royaume-Uni' },
  { code: '+32',  flag: '🇧🇪', label: 'Belgique' },
  { code: '+41',  flag: '🇨🇭', label: 'Suisse' },
  { code: '+49',  flag: '🇩🇪', label: 'Allemagne' },
  { code: '+34',  flag: '🇪🇸', label: 'Espagne' },
  { code: '+39',  flag: '🇮🇹', label: 'Italie' },
  { code: '+213', flag: '🇩🇿', label: 'Algérie' },
  { code: '+216', flag: '🇹🇳', label: 'Tunisie' },
]
const countryCode = ref(localStorage.getItem('pexitalk-country') || '+1')
const currentCountry = computed(() => COUNTRIES.find((c) => c.code === countryCode.value) || COUNTRIES[0])
const numberInput = ref<HTMLInputElement | null>(null)

watch(countryCode, (v) => localStorage.setItem('pexitalk-country', v))

function onNumberInput(e: Event) {
  const raw = (e.target as HTMLInputElement).value
  // Garde uniquement chiffres, *, #, +
  const cleaned = raw.replace(/[^\d*#+]/g, '')
  callStore.setDial(cleaned)
}

// ── Lookup contact en temps réel ────────────────────────────────────────
// Quand une sonnerie entre (inbound) ou qu'un agent compose un numéro
// connu, on interroge l'API pour afficher les méta-données.
const contactLookup = ref<ContactLookup | null>(null)
const lookupLoading = ref(false)
const lastLookupPhone = ref('')

const showContactCard = computed(
  () => callStore.callStatus !== 'idle' && !!callStore.dialNumber,
)

let lookupDebounce: ReturnType<typeof setTimeout> | null = null

async function lookupContact(phone: string) {
  if (!phone || phone === lastLookupPhone.value) return
  // On attend qu'on ait au moins 7 chiffres pour éviter de spammer l'API
  // pendant la frappe.
  const digits = phone.replace(/\D/g, '')
  if (digits.length < 7) {
    contactLookup.value = null
    lastLookupPhone.value = ''
    return
  }
  lookupLoading.value = true
  try {
    contactLookup.value = await QueueAPI.lookup(phone)
    // On ne marque le numéro « déjà cherché » QU'EN CAS DE SUCCÈS : sinon un
    // 1er lookup échoué (réseau) laissait `lastLookupPhone` posé → la garde en
    // tête bloquait tout nouvel essai et la fiche restait vide (« parfois vide
    // en Aperçu »).
    lastLookupPhone.value = phone
  } catch {
    contactLookup.value = null
    lastLookupPhone.value = ''   // autorise un nouvel essai au prochain déclenchement
  } finally {
    lookupLoading.value = false
  }
}

watch(
  () => [callStore.callStatus, callStore.dialNumber] as const,
  ([status, num]) => {
    if (status === 'idle') {
      contactLookup.value = null
      lastLookupPhone.value = ''
      return
    }
    // Debounce 300ms pour ne pas rechercher à chaque DTMF / chiffre composé
    if (lookupDebounce) clearTimeout(lookupDebounce)
    lookupDebounce = setTimeout(() => lookupContact(num), 300)
  },
  { immediate: false },
)

// ── Statut « dial-check » (liste noire + client) pendant la COMPOSITION ──────
// Indépendant du lookup ci-dessus (qui ne tourne pas au repos) : on veut le
// badge « Client » + les campagnes même quand on tape un numéro manuellement.
const dialCheck = ref<DialCheck | null>(null)
let dialCheckDebounce: ReturnType<typeof setTimeout> | null = null
let lastDialCheckPhone = ''

async function runDialCheck(phone: string) {
  const digits = (phone || '').replace(/\D/g, '')
  if (digits.length < 7) { dialCheck.value = null; lastDialCheckPhone = ''; return }
  if (phone === lastDialCheckPhone) return
  lastDialCheckPhone = phone
  try {
    dialCheck.value = await QueueAPI.dialCheck(phone, dialer.campaign?.id)
  } catch { /* silencieux : le filet bloquant reste dans startCall */ }
}

watch(
  () => callStore.dialNumber,
  (num) => {
    if (!num) { dialCheck.value = null; lastDialCheckPhone = ''; return }
    if (dialCheckDebounce) clearTimeout(dialCheckDebounce)
    dialCheckDebounce = setTimeout(() => runDialCheck(num), 300)
  },
)

// Numéro « client » : vrai dès qu'une entrée est marquée client (même sans
// campagne — ex. tagué en appel manuel). Les campagnes listées sont celles où
// il est client (peut être vide).
const isClientNumber = computed(
  () => dialCheck.value?.is_client || (callStore.selectedEntry?.is_client ?? false),
)
const clientCampaigns = computed(() => dialCheck.value?.client_campaigns ?? [])

// ── Suggestions de numéros pendant la composition (hors appel) ──────────
// Dès le 8e chiffre, on propose les numéros connus correspondants avec leur
// dernier traitement, pour choisir/rappeler sans tout retaper.
const suggestions = ref<QueueSuggestion[]>([])
const showSuggestions = ref(false)
const pickedPhone = ref('')
let suggestDebounce: ReturnType<typeof setTimeout> | null = null

watch(
  () => [callStore.isActive, callStore.dialNumber] as const,
  ([active, num]) => {
    // Pas de suggestions pendant un appel.
    if (active) { suggestions.value = []; showSuggestions.value = false; return }
    // On vient de choisir ce numéro → ne pas ré-ouvrir la liste.
    if (num && num === pickedPhone.value) { showSuggestions.value = false; return }
    const digits = (num || '').replace(/\D/g, '')
    if (digits.length < 8) { suggestions.value = []; showSuggestions.value = false; return }
    if (suggestDebounce) clearTimeout(suggestDebounce)
    suggestDebounce = setTimeout(async () => {
      try {
        suggestions.value = await QueueAPI.suggest(num)
        showSuggestions.value = suggestions.value.length > 0
      } catch {
        suggestions.value = []
        showSuggestions.value = false
      }
    }, 250)
  },
  { immediate: false },
)

function pickSuggestion(s: QueueSuggestion) {
  pickedPhone.value = s.phone
  callStore.setDial(s.phone)
  showSuggestions.value = false
  suggestions.value = []
}

function dialAnother(phone: string) {
  // L'agent clique un autre numéro dans la carte contact → on prépare l'appel
  callStore.setDial(phone)
}

let sip: SipController | null = null
const callDirection = ref<'inbound' | 'outbound' | null>(null)

const isIncomingRinging = computed(
  () => callDirection.value === 'inbound' && callStore.callStatus === 'ringing'
)

const sipState = ref<import('../controllers/sip_controller').SipState>('idle')

const sipStateLabel = computed(() => ({
  idle: 'Inactif',
  connecting: 'Connexion…',
  connected: 'Connecté',
  registered: 'Prêt',
  registration_failed: 'Échec',
  disconnected: 'Coupé',
}[sipState.value] || ''))

const sipStateTitle = computed(() => ({
  idle: 'Téléphone inactif',
  connecting: 'Connexion au serveur SIP en cours',
  connected: 'WebSocket connecté — enregistrement en cours',
  registered: 'Enregistré, prêt à appeler',
  registration_failed: 'Échec d\'enregistrement — identifiants SIP refusés',
  disconnected: 'WebSocket coupé — tentative de reconnexion',
}[sipState.value] || ''))

function buildController() {
  if (sip) return
  const u = auth.user
  if (!u?.username || !u?.sip_password || !u?.sip_host) return
  sip = new SipController({
    host: u.sip_host,
    username: u.username,
    password: u.sip_password,
    wsPath: u.sip_ws_path || '/ws',
  })
  sip.onCallChange(async (call) => {
    // Tout évènement d'appel = activité — empêche qu'un appel entrant
    // arrive pile au moment où l'auto-transition allait basculer en busy.
    userActivity.markActive()
    callDirection.value = call.direction
    if (call.status === 'connecting' || call.status === 'ringing') {
      callStore.callStatus = call.direction === 'inbound' ? 'ringing' : 'dialing'
      if (call.direction === 'outbound') {
        await agentStore.change('in_call').catch(() => {})
      }
    } else if (call.status === 'in_call') {
      callStore.answered()
      await agentStore.change('in_call').catch(() => {})
    } else if (call.status === 'ended') {
      callDirection.value = null
      fallbackLine.value = null
      const answered = !!callStore.callAnsweredAt
      // Appel NON décroché en session auto → le système le classe (occupé /
      // pas de réponse / injoignable), raccroche et enchaîne, SANS modale ni
      // clic. Les appels DÉCROCHÉS, eux, ouvrent la fiche et attendent que
      // l'agent clique « Terminer ». Activable par campagne (`skip_voicemail`,
      // défaut on) — `!== false` préserve le comportement si la valeur manque.
      const skipOn = dialer.campaign?.skip_voicemail !== false
      if (!answered && dialer.isActive && skipOn && await tryAutoClassify(call.failureCause)) {
        await agentStore.change('available').catch(() => {})
        return
      }
      callStore.endCall()
      const next = callStore.callAnsweredAt ? 'wrap_up' : 'available'
      await agentStore.change(next).catch(() => {})
    }
    if (call.direction === 'inbound' && call.remoteNumber) {
      callStore.setDial(call.remoteNumber)
    }
  })
  // État du registre SIP — exposé dans le header du panneau
  sip.onStateChange((s) => {
    sipState.value = s
    if (s === 'registration_failed') {
      toasts.error(
        'Téléphone non connecté',
        'Identifiants SIP refusés par le serveur. Vérifiez vos paramètres ou contactez un admin.',
      )
    }
  })
  // Erreurs catégorisées (permission micro, échec d'appel) → toasts
  sip.onError((err) => {
    if (err.kind === 'media_denied' || err.kind === 'media_unavailable') {
      toasts.warning('Micro indisponible', err.message)
    } else if (err.kind === 'not_registered' || err.kind === 'no_ua') {
      toasts.warning('Téléphone non prêt', err.message)
    } else if (err.kind === 'call_failed') {
      toasts.error('Appel échoué', err.message)
    }
  })
  sip.initialize()
  // Demande proactive de la permission micro — évite la 1ère sonnerie
  // ratée parce que le browser attend la réponse au prompt.
  sip.ensureMicrophoneAccess().catch(() => {})
}

const keys = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '*', '0', '#']
// Superviseur/admin/owner : composent librement quel que soit leur statut —
// ils ne suivent pas le cycle agent (disponible → en appel → post-appel).
const canCall = computed(() =>
  auth.isTeamAdmin
  || agentStore.current === 'available'
  || agentStore.current === 'wrap_up',
)
const callTitle = computed(() => canCall.value ? "Démarrer l'appel" : 'Statut non disponible')

const statusLabel = computed(() => ({
  idle: 'Prêt',
  dialing: 'Connexion',
  ringing: 'Sonne',
  in_call: 'En appel',
  on_hold: 'En attente',
  ended: 'Terminé',
}[callStore.callStatus] || ''))

// Nom de la ligne de secours quand l'appel en cours est parti par le SIP par
// défaut (ligne habituelle en panne). null = appel normal / pas d'appel.
const fallbackLine = ref<string | null>(null)

const duration = computed(() => {
  if (!callStore.callAnsweredAt) return '00:00'
  const start = new Date(callStore.callAnsweredAt).getTime()
  const d = Math.max(0, now.value - start)
  const m = Math.floor(d / 60000)
  const s = Math.floor((d % 60000) / 1000)
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`
})

function initial(name?: string) { return (name || '?').charAt(0).toUpperCase() }
function press(k: string) { callStore.setDial(callStore.dialNumber + k) }
function backspace() { callStore.setDial(callStore.dialNumber.slice(0, -1)) }

function buildOutgoingNumber(raw: string): string {
  // Extension interne (3 à 4 chiffres ne commençant pas par 0, ex. 100, 101,
  // 300, 1001) → JAMAIS d'indicatif, on compose tel quel. Élargi depuis `1XX`
  // qui ratait les extensions à 4 chiffres (1001 devenait 00+1+1001).
  if (/^[1-9]\d{2,3}$/.test(raw)) return raw
  // Déjà préfixé international
  if (raw.startsWith('+')) return raw.replace('+', '00')
  if (raw.startsWith('00')) return raw
  // 0 puis 9 chiffres (FR) → garde tel quel
  if (raw.startsWith('0') && raw.length >= 9) return raw
  // « Sans indicatif » sélectionné → numéro composé tel quel (déjà complet)
  if (!countryCode.value) return raw
  // Sinon préfixe avec l'indicatif pays sélectionné
  return '00' + countryCode.value.replace('+', '') + raw
}

async function startCall() {
  if (!callStore.canDial || !canCall.value) return
  const phone = callStore.dialNumber.trim()
  // Vérif SYNCHRONE juste avant de composer (manuellement inclus) — et NON via
  // `contactLookup`, qui n'est pas renseigné au repos. Renvoie liste noire +
  // statut client (par-campagne), match cross-format côté serveur.
  let check: DialCheck
  try {
    check = await QueueAPI.dialCheck(phone, dialer.campaign?.id)
  } catch {
    // Vérif impossible (réseau) → on bloque PAR SÉCURITÉ (do-not-call).
    toasts.error('Vérification impossible', 'Impossible de vérifier le numéro (liste noire / client) — appel bloqué. Réessayez.')
    return
  }
  // Liste noire : on ne compose JAMAIS un numéro blacklisté.
  if (check.is_blacklisted) {
    toasts.error(
      'Numéro en liste noire',
      'Ce numéro est en liste noire (à ne plus jamais appeler) — appel bloqué.',
    )
    return
  }
  // Client : confirmation OBLIGATOIRE (popup persistante, fermée seulement par
  // la croix/Annuler) listant la fiche contact + les campagnes concernées.
  if (check.is_client) {
    const c = check.contact || {}
    const details: { k: string; v: string }[] = [{ k: 'Numéro', v: phone }]
    if (c.name) details.push({ k: 'Nom', v: c.name })
    if (c.company) details.push({ k: 'Société', v: c.company })
    if (check.client_campaigns.length) {
      details.push({
        k: check.client_campaigns.length > 1 ? 'Client des campagnes' : 'Client de',
        v: check.client_campaigns.map((cc) => cc.name).join(', '),
      })
    }
    const ok = await confirm.ask({
      title: '⭐ Ce numéro est un client',
      message: 'Voulez-vous vraiment contacter ce client ?',
      kind: 'warning',
      confirmLabel: 'Appeler le client',
      cancelLabel: 'Annuler',
      details,
      requireExplicitClose: true,
    })
    if (!ok) return
  }
  if (!sip) {
    toasts.warning(
      'Téléphone non prêt',
      'Poste SIP non configuré — reconnectez-vous ou contactez un administrateur.',
    )
    return
  }
  let dialed = buildOutgoingNumber(callStore.dialNumber.trim())
  QueueAPI.heartbeat().catch(() => {})
  // Trunk + caller-id résolus côté backend (campagne > utilisateur > équipe >
  // global). SANS ça, le dialplan retombait sur GLOBAL(PEXI_TRUNK) : tous les
  // appels passaient par l'unique trunk global, ignorant le trunk dédié de
  // l'agent/campagne (d'où le 503 cause 34 dès 2 appels simultanés sur un
  // trunk mono-canal). En cas d'échec réseau, on laisse le dialplan décider.
  // On passe AUSSI le numéro composé : le backend le normalise selon le format
  // du trunk résolu (ex: +E.164 pour Telnyx) et renvoie `dialed_number`.
  let cid = ''
  let trunk = ''
  fallbackLine.value = null
  try {
    const routing = await TelephonyAPI.outboundRouting(dialer.campaign?.id, dialed)
    cid = routing.caller_id || ''
    trunk = routing.trunk_endpoint || ''
    if (routing.dialed_number) dialed = routing.dialed_number
    // Ligne normale en panne → le backend a routé sur le SIP par défaut
    // (ligne de secours) : on prévient l'agent et on affiche le badge.
    if (routing.fallback) {
      const name = routing.trunk_name || 'ligne de secours'
      fallbackLine.value = name
      toasts.warning(
        'Ligne de secours',
        `« ${routing.fallback_from_name || 'Ligne habituelle'} » en panne — appel via « ${name} »`,
      )
    }
  } catch {
    /* repli silencieux sur le trunk/caller-id global du dialplan */
  }
  // call() vérifie la registration et la permission micro, et signale tout
  // échec via onError (toast). L'UI ne bascule en mode appel QUE si
  // l'INVITE est réellement parti — sinon le panneau affichait un appel
  // en cours alors que rien n'existait côté Asterisk.
  const ok = await sip.call(dialed, cid, trunk)
  if (ok) callStore.startCall()
}

function toggleMute() {
  callStore.muted = !callStore.muted
  if (sip) callStore.muted ? sip.mute() : sip.unmute()
}
function toggleHold() {
  callStore.onHold = !callStore.onHold
  callStore.callStatus = callStore.onHold ? 'on_hold' : 'in_call'
  if (sip) callStore.onHold ? sip.hold() : sip.unhold()
}

async function hangup() {
  if (sip) {
    sip.hangup()
  } else {
    callStore.endCall()
    await agentStore.change('wrap_up').catch(() => {})
  }
}

async function answer() {
  // answer() est désormais async : il demande la permission micro
  // avant d'envoyer le 200 OK. Si le micro est refusé, le SipController
  // raccroche poliment côté Asterisk avec un 486 Busy.
  if (sip) await sip.answer()
}

// ── Raccourcis clavier pendant un appel ─────────────────────────────────
useKeyboardShortcuts([
  {
    // Espace = décrocher quand ça sonne (entrant uniquement)
    key: ' ',
    description: 'Décrocher (appel entrant)',
    handler: () => {
      if (isIncomingRinging.value) answer()
    },
  },
  {
    // Escape = raccrocher / refuser
    key: 'Escape',
    description: 'Raccrocher / refuser',
    skipInInputs: false, // Escape doit fonctionner même dans un input
    handler: () => {
      if (callStore.isActive) hangup()
    },
  },
  {
    // Ctrl/Cmd + M = mute toggle
    key: 'm',
    ctrl: true,
    description: 'Couper / activer le micro',
    handler: () => {
      if (callStore.callStatus === 'in_call') toggleMute()
    },
  },
])

const showDtmf = ref(false)
const scriptOpen = ref(false)
const notebookOpen = ref(false)
const showNote = ref(false)

// ── Transcription live ───────────────────────────────────────────────────
const transcription = useTranscription()
const transcriptionScroll = ref<HTMLElement | null>(null)

// Autorise la transcription uniquement si l'entrée vient d'une campagne
// avec transcription_enabled=true. La vérification réelle est côté backend ;
// ici on désactive simplement le bouton si on sait que ce n'est pas le cas.
const canTranscribe = computed(() => {
  if (!callStore.isActive) return false
  if (!callStore.selectedEntry) return false
  // Le champ transcription_enabled n'est pas sur QueueEntry directement ;
  // il est sur la campagne. On expose via selectedEntry si dispo, sinon
  // on laisse le backend trancher (bouton actif, le backend renverra denied).
  return true
})

const transcriptionTitle = computed(() => {
  if (!callStore.isActive) return 'Appel inactif'
  if (transcription.status.value === 'active') return 'Arrêter la transcription'
  if (transcription.status.value === 'denied') return transcription.deniedReason.value
  return 'Démarrer la transcription live'
})

async function toggleTranscription() {
  if (transcription.status.value === 'active') {
    transcription.stop()
  } else {
    if (!sip) return
    await transcription.start(callStore.dialNumber, sip)
  }
}

// Scroll automatique quand de nouvelles lignes arrivent
watch(() => transcription.lines.value.length, () => {
  nextTick(() => {
    if (transcriptionScroll.value) {
      transcriptionScroll.value.scrollTop = transcriptionScroll.value.scrollHeight
    }
  })
})

// Arrêt propre de la transcription quand l'appel se termine (+ effacement du
// badge « secours », qui ne vaut que pour l'appel en cours)
watch(() => callStore.callStatus, (status) => {
  if (status === 'ended' || status === 'idle') {
    transcription.stop()
    showDtmf.value = false
    dtmfSent.value = ''
    fallbackLine.value = null
  }
})
// ── Pavé DTMF (suivi des serveurs vocaux pendant l'appel) ─────────────────
// Le pavé est focusable : pendant qu'il a le focus, les touches 0-9 * # du
// CLAVIER PHYSIQUE envoient un DTMF (sans interférer avec la saisie des fiches,
// qui se fait dans un autre composant). `dtmfSent` affiche la séquence émise.
const dtmfPad = ref<HTMLElement | null>(null)
const dtmfSent = ref('')

// ── Volume du micro (en %) ────────────────────────────────────────────────
// Réglé en direct par l'agent ; persistant (localStorage, lu par le contrôleur
// SIP). 100 % = micro normal, 200 % = ×2. Agit sur la voix transmise.
function loadMicVolume(): number {
  try {
    const v = Number(localStorage.getItem('pexitalk:micGain'))
    if (Number.isFinite(v) && v >= 0) return Math.round(Math.min(2, v) * 100)
  } catch { /* localStorage indisponible */ }
  return 100
}
const micVolume = ref(loadMicVolume())
function onMicVolume(e: Event) {
  const pct = Number((e.target as HTMLInputElement).value)
  micVolume.value = pct
  sip?.setMicGain(pct / 100)
}

// ── Haut-parleur : voix du correspondant (volume + clarté) ────────────────
// Local à l'agent (sa propre écoute), persistant via le contrôleur SIP.
// 100 % = normal, 200 % = ×2 (boost). Clarté = passe-haut + compresseur léger.
function loadSpeakerVolume(): number {
  try {
    const v = Number(localStorage.getItem('pexitalk:speakerVol'))
    if (Number.isFinite(v) && v >= 0) return Math.round(Math.min(2, v) * 100)
  } catch { /* localStorage indisponible */ }
  return 100
}
const speakerVolume = ref(loadSpeakerVolume())
function onSpeakerVolume(e: Event) {
  const pct = Number((e.target as HTMLInputElement).value)
  speakerVolume.value = pct
  sip?.setSpeakerVolume(pct / 100)
}

// ── Bloc audio hors appel + volume de la sonnerie ─────────────────────────
// La sonnerie a son propre volume : la monter pour entendre les appels ne doit
// pas faire hurler la voix du correspondant. Aperçu sonore pendant le réglage.
const idleAudioOpen = ref(false)
const ringVolume = ref(loadRingVolume())
const ringPreview = new Ringtone()
function onRingVolume(e: Event) {
  const pct = Number((e.target as HTMLInputElement).value)
  ringVolume.value = pct
  saveRingVolume(pct)
  if (pct === 0) { ringPreview.stop(); return }
  void ringPreview.start(pct, loadOutputDeviceId())
  ringPreview.setVolume(pct)
}
function stopRingPreview() { ringPreview.stop() }

const clarityOn = ref(localStorage.getItem('pexitalk:speakerClarity') === '1')
function onToggleClarity() {
  clarityOn.value = !clarityOn.value
  sip?.setRemoteClarity(clarityOn.value)
}

// ── Filtre micro (noise gate « voix proche ») ─────────────────────────────
// Ne transmet le micro que quand son niveau dépasse un seuil → seule la voix
// proche du casque passe, pas un voisin lointain. Réglable à l'oreille via le
// vumètre : on place le seuil ENTRE le niveau de l'agent (barre verte) et celui
// du voisin (qui doit rester sous le repère).
const GATE_DB_MIN = -55  // gauche = permissif (laisse passer presque tout)
const GATE_DB_MAX = -25  // droite = strict (il faut parler fort/près)
function loadGateDb(): number {
  try {
    const v = Number(localStorage.getItem('pexitalk:micGateDb'))
    if (Number.isFinite(v) && v < 0) return Math.max(GATE_DB_MIN, Math.min(GATE_DB_MAX, v))
  } catch { /* localStorage indisponible */ }
  return -38
}
const micGateDb = ref(loadGateDb())
function onMicGateDb(e: Event) {
  const db = Number((e.target as HTMLInputElement).value)
  micGateDb.value = db
  sip?.setMicGateDb(db)
}

// ── Anti-bruit ↔ Clarté ────────────────────────────────────────────────────
// Dose le filtrage du bruit en direct (0 = clarté max / voix naturelle,
// 3 = anti-bruit max). Appliqué pendant l'appel sans recouper le micro.
const DENOISE_LABELS = ['Clarté max', 'Doux', 'Fort', 'Anti-bruit max']
const DENOISE_MAX = DENOISE_LABELS.length - 1
function loadDenoise(): number {
  try {
    const v = Number(localStorage.getItem('pexitalk:micDenoise'))
    if (Number.isInteger(v) && v >= 0 && v <= DENOISE_MAX) return v
  } catch { /* localStorage indisponible */ }
  return 1  // « Doux » : défaut validé (clair + sans bruit).
}
const micDenoise = ref(loadDenoise())
function onMicDenoise(e: Event) {
  const v = Number((e.target as HTMLInputElement).value)
  micDenoise.value = v
  sip?.setMicDenoiseLevel(v)
}

// Réglages micro CENTRAUX (par tenant) : le owner les pousse au serveur, tous
// les agents les reçoivent au démarrage (lecture seule). On persiste au
// RELÂCHEMENT du slider (@change), pas à chaque pixel (@input).
function persistMicSettings() {
  if (!canEditMicSettings.value) return
  MicAPI.set({ denoise_level: micDenoise.value, gate_db: micGateDb.value })
    .catch(() => { /* best-effort ; le réglage local reste appliqué */ })
}
async function loadCentralMicSettings() {
  try {
    const s = await MicAPI.get()
    micDenoise.value = s.denoise_level
    micGateDb.value = s.gate_db
    // Applique au contrôleur (live si appel en cours) + mémorise en local.
    sip?.setMicDenoiseLevel(s.denoise_level)
    sip?.setMicGateDb(s.gate_db)
  } catch { /* hors-ligne : on garde les valeurs locales/défaut */ }
}

// Vumètre temps réel : niveau micro (%) + état du gate (vert si ouvert).
const micLevelPct = ref(0)
const gateOpen = ref(false)
const micTesting = ref(false)
// Panneau réglages micro replié par défaut → panneau Téléphone épuré.
const micPanelOpen = ref(false)
const METER_DB_MIN = -60
const METER_DB_MAX = -10
function dbToPct(db: number): number {
  return Math.max(0, Math.min(100, ((db - METER_DB_MIN) / (METER_DB_MAX - METER_DB_MIN)) * 100))
}
const gateThreshPct = computed(() => dbToPct(micGateDb.value))
let meterRaf = 0
function meterTick() {
  if (sip?.isMicActive()) {
    micLevelPct.value = dbToPct(sip.getMicLevelDb())
    gateOpen.value = sip.isGateOpen()
  } else if (micLevelPct.value !== 0) {
    micLevelPct.value = 0
    gateOpen.value = false
  }
  meterRaf = requestAnimationFrame(meterTick)
}
async function toggleMicTest() {
  if (micTesting.value) {
    sip?.stopMicTest()
    micTesting.value = false
  } else {
    micTesting.value = !!(await sip?.startMicTest())
  }
}
onMounted(() => {
  meterRaf = requestAnimationFrame(meterTick)
  loadCentralMicSettings()
})
onUnmounted(() => {
  ringPreview.stop()
})

onUnmounted(() => {
  cancelAnimationFrame(meterRaf)
  if (micTesting.value) { sip?.stopMicTest(); micTesting.value = false }
})

function sendDtmf(k: string) {
  if (sip) sip.dtmf(k)
  dtmfSent.value = (dtmfSent.value + k).slice(-32)
}

// L'agent est-il en train de saisir dans un champ ? Si oui, on NE capte PAS la
// touche en DTMF (il écrit un nom, une note…). Sinon, tant que le pavé est
// ouvert, n'importe quelle touche 0-9 * # part en DTMF — sans dépendre du focus
// (le formulaire de la fiche en plein écran volait le focus du pavé).
function isTypingInField(): boolean {
  const el = document.activeElement as HTMLElement | null
  if (!el) return false
  const tag = el.tagName
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || el.isContentEditable
}

function onDtmfKey(e: KeyboardEvent) {
  if (!showDtmf.value) return
  if (e.metaKey || e.ctrlKey || e.altKey) return
  if (isTypingInField()) return
  if (/^[0-9*#]$/.test(e.key)) {
    e.preventDefault()
    sendDtmf(e.key)
  }
}

// Le pavé ouvert installe un écouteur clavier GLOBAL (document) : l'agent peut
// suivre un serveur vocal en tapant directement, où qu'il soit dans la page.
watch(showDtmf, (open) => {
  if (open) {
    dtmfSent.value = ''
    document.addEventListener('keydown', onDtmfKey)
    nextTick(() => dtmfPad.value?.focus())
  } else {
    document.removeEventListener('keydown', onDtmfKey)
  }
})

const transferOpen = ref(false)
function askTransfer() { transferOpen.value = true }
async function doTransfer(target: string) {
  if (!target) return
  // Transfert AVEUGLE via le serveur (ARI) : le REFER SIP côté navigateur ne
  // partait pas. Le backend résout notre canal et redirige le client vers
  // l'extension cible ; notre jambe se termine ensuite normalement (BYE).
  try {
    await TelephonyAPI.transferMyCall(target)
    toasts.info('Transfert en cours', `Mise en relation avec ${target}…`)
  } catch (e: any) {
    toasts.error(
      'Transfert échoué',
      e?.response?.data?.detail || 'Destinataire injoignable — réessayez.',
    )
  }
}

let timer: ReturnType<typeof setInterval> | null = null
onMounted(() => {
  timer = setInterval(() => { now.value = Date.now() }, 1000)
  buildController()
})
watch(() => auth.user?.sip_password, () => buildController())
watch(() => callStore.autoCallToken, (v) => { if (v > 0) startCall() })
onUnmounted(() => {
  if (timer) clearInterval(timer)
  document.removeEventListener('keydown', onDtmfKey)
  if (sip) sip.destroy()
})
</script>

<style scoped>
.panel {
  width: 300px;
  min-width: 300px;
  background: var(--surface);
  border-left: 1px solid var(--border);
  padding: 14px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  overflow: hidden;
}

/* La contact-card injectée a son propre fond clair et sa propre bordure ;
   on lui donne juste un overflow-y au cas où le contenu dépasse. */
.panel-contact-card {
  overflow-y: auto;
  max-height: 40vh;
  flex-shrink: 1;
}
.agent-tools { display: grid; grid-template-columns: repeat(4, 1fr); gap: 5px; margin: 2px 0; }
.tool-btn {
  display: inline-flex; align-items: center; justify-content: center; gap: 5px;
  padding: 7px 6px;
  background: var(--surface); border: 1px solid var(--border); border-radius: var(--radius);
  font-size: 11.5px; font-weight: 600; color: var(--text-secondary); cursor: pointer;
}
.tool-btn:hover:not(:disabled) { border-color: var(--primary); color: var(--primary); background: var(--primary-50); }
.tool-btn.active { border-color: var(--primary); color: var(--primary); background: var(--primary-50); }
.tool-btn:disabled { opacity: 0.45; cursor: not-allowed; }
.call-note {
  width: 100%; box-sizing: border-box; margin: 2px 0;
  padding: 9px 11px; border: 1px solid var(--border); border-radius: var(--radius);
  font-size: 13px; font-family: var(--font, inherit); resize: vertical; background: var(--surface); color: var(--text);
}
.call-note:focus { outline: none; border-color: var(--primary); box-shadow: var(--ring); }

.head { display: flex; justify-content: space-between; align-items: center; }
.title {
  font-size: 10.5px;
  font-weight: 700;
  color: var(--text-muted);
  text-transform: uppercase;
  letter-spacing: 0.07em;
}

.state {
  display: flex; align-items: center; gap: 5px;
  padding: 3px 8px;
  border-radius: var(--radius-full);
  font-size: 11px;
  font-weight: 600;
  color: var(--text-muted);
  background: var(--surface-hover);
  border: 1px solid var(--border);
}
.state-dot { width: 6px; height: 6px; border-radius: 50%; background: var(--text-muted); }
.state[data-status='in_call'] { background: var(--success-bg); color: var(--success); border-color: var(--success-border); }
.state[data-status='in_call'] .state-dot { background: var(--success); animation: pulse-dot 1.5s infinite; }
.state[data-status='dialing'] .state-dot,
.state[data-status='ringing'] .state-dot { background: var(--warning); }
.state[data-status='dialing'], .state[data-status='ringing'] { background: var(--warning-bg); color: var(--warning); border-color: var(--warning-border); }
.state[data-status='on_hold'] { background: var(--primary-100); color: var(--primary); border-color: var(--primary-200); }
.state[data-status='on_hold'] .state-dot { background: var(--primary); }
/* Appel parti par la ligne de secours (SIP par défaut) — ambre discret. */
.state[data-status='fallback'] { gap: 4px; background: var(--warning-bg); color: var(--warning); border-color: var(--warning-border); white-space: nowrap; }

@keyframes pulse-dot {
  0%, 100% { box-shadow: 0 0 0 0 rgba(22,163,74,0.5); }
  50% { box-shadow: 0 0 0 4px rgba(22,163,74,0); }
}

.display {
  background: linear-gradient(150deg, #5B21B6 0%, #6D28D9 38%, #7C3AED 72%, #8B5CF6 100%);
  color: white;
  border-radius: var(--radius-xl);
  padding: 18px 14px 20px;
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  box-shadow: 0 8px 24px rgba(124,58,237,0.38), inset 0 1px 0 rgba(255,255,255,0.16);
  position: relative;
  overflow: hidden;
}
.display::before {
  content: '';
  position: absolute;
  inset: 0;
  background: radial-gradient(ellipse at 30% 0%, rgba(255,255,255,0.10) 0%, transparent 60%);
  pointer-events: none;
}

.contact { display: flex; flex-direction: column; align-items: center; gap: 6px; margin-bottom: 4px; }
.contact-avatar {
  width: 38px; height: 38px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 50%;
  display: flex; align-items: center; justify-content: center;
  font-size: 14px;
  font-weight: 600;
}
.contact-name { font-size: 13px; font-weight: 500; }

.number {
  font-family: var(--font-mono);
  font-size: 24px;
  font-weight: 500;
  letter-spacing: 0.02em;
  color: white;
}
.number.ph { color: rgba(255, 255, 255, 0.25); font-size: 14px; font-family: var(--font); font-weight: 400; }

/* Bannière « client » dans le composeur (fond clair via le voile doré). */
.client-banner {
  display: flex; align-items: center; gap: 10px;
  width: 100%; box-sizing: border-box;
  margin: 2px 0;
  padding: 8px 10px;
  border-radius: var(--radius-md);
  background: linear-gradient(135deg, rgba(253,230,138,0.22), rgba(245,158,11,0.18));
  border: 1px solid rgba(245,158,11,0.55);
}
.client-banner .cb-txt { display: flex; flex-direction: column; line-height: 1.2; min-width: 0; }
.client-banner .cb-title { font-size: 12px; font-weight: 800; color: #FCD34D; letter-spacing: 0.03em; }
.client-banner .cb-camps { font-size: 11px; color: rgba(255,255,255,0.75); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }

.number-row {
  display: flex; gap: 6px; width: 100%;
  align-items: center;
}
.country {
  background: rgba(255, 255, 255, 0.15);
  color: white;
  border: 1px solid rgba(255, 255, 255, 0.25);
  border-radius: var(--radius-sm);
  padding: 6px 4px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  outline: none;
  flex-shrink: 0;
}
.country option { color: var(--text); background: var(--surface); }
.number-input {
  flex: 1; min-width: 0;
  background: rgba(255, 255, 255, 0.12);
  color: white;
  border: 1px solid rgba(255, 255, 255, 0.25);
  border-radius: var(--radius-sm);
  padding: 8px 10px;
  font-family: var(--font-mono);
  font-size: 18px;
  font-weight: 500;
  outline: none;
  text-align: center;
}
.number-input::placeholder { color: rgba(255, 255, 255, 0.4); font-family: var(--font); font-size: 13px; font-weight: 400; }
.number-input:focus { border-color: rgba(255, 255, 255, 0.6); background: rgba(255, 255, 255, 0.18); }

/* Suggestions de numéros (composition) — sur fond clair du composeur */
.suggest-list {
  list-style: none; margin: 6px 0 0; padding: 4px;
  background: rgba(255, 255, 255, 0.97);
  border-radius: 10px;
  max-height: 230px; overflow-y: auto;
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.25);
}
.suggest-item {
  display: flex; align-items: center; justify-content: space-between; gap: 10px;
  padding: 7px 9px; border-radius: 7px; cursor: pointer;
}
.suggest-item:hover { background: var(--primary-100, rgba(37, 99, 235, 0.1)); }
.sg-main { display: flex; align-items: center; gap: 8px; min-width: 0; }
.sg-phone { font-size: 12.5px; font-weight: 600; color: var(--text); }
.sg-name { font-size: 12px; color: var(--text-secondary); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; max-width: 120px; }
.sg-camp { font-size: 10.5px; color: var(--text-muted); background: var(--surface-hover); padding: 1px 6px; border-radius: 8px; white-space: nowrap; }
.sg-meta { display: flex; align-items: center; gap: 6px; flex-shrink: 0; }
.sg-dispo { display: inline-flex; align-items: center; gap: 4px; font-size: 11px; font-weight: 600; color: var(--text); }
.sg-dot { width: 7px; height: 7px; border-radius: 50%; }
.sg-att { font-size: 10.5px; color: var(--text-muted); }
.mono { font-family: var(--font-mono); }

.timer {
  font-family: var(--font-mono);
  font-size: 13px;
  color: rgba(255, 255, 255, 0.6);
}

/* Dialpad */
.pad { display: grid; grid-template-columns: repeat(3, 1fr); gap: 5px; }
.key {
  background: linear-gradient(180deg, var(--surface), var(--surface-hover));
  border: 1px solid var(--border);
  border-radius: var(--radius-md);
  padding: 10px;
  font-size: 17px;
  font-weight: 600;
  color: var(--text);
  cursor: pointer;
  box-shadow: var(--shadow-xs);
  transition: background var(--duration-fast) var(--ease-out), border-color var(--duration-fast) var(--ease-out), box-shadow var(--duration-fast) var(--ease-out), transform var(--duration-fast) var(--ease-out);
  min-height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  letter-spacing: 0;
}
.key:hover:not(:disabled) { background: var(--surface); border-color: var(--primary-200); box-shadow: var(--shadow-sm); transform: translateY(-1px); color: var(--primary); }
.key:active:not(:disabled) { transform: scale(0.95); background: var(--surface-active); box-shadow: none; }
.key:disabled { opacity: 0.3; cursor: not-allowed; }

.key-act { color: var(--text-secondary); background: var(--bg); }
.key-act:hover:not(:disabled) { background: var(--surface-hover); }
.key-call {
  background: var(--success);
  color: white;
  border-color: transparent;
  box-shadow: 0 2px 8px rgba(22,163,74,0.35);
}
.key-call:hover:not(:disabled) { background: #15803D; box-shadow: 0 3px 12px rgba(22,163,74,0.45); }
.key-call:disabled { background: var(--surface-active); color: var(--text-disabled); box-shadow: none; border-color: var(--border); }

/* Controls */
.controls { display: flex; flex-direction: column; gap: 8px; }
.ctrl-row { display: grid; grid-template-columns: repeat(4, 1fr); gap: 5px; }
.ctrl {
  display: flex; flex-direction: column; align-items: center; gap: 3px;
  padding: 9px 4px;
  background: var(--surface-hover);
  border: none;
  border-radius: var(--radius);
  cursor: pointer;
  color: var(--text-secondary);
  font-size: 11px;
  font-weight: 500;
  transition: all 0.1s;
}
.ctrl:hover { background: var(--surface-active); color: var(--text); }
.ctrl.active { background: var(--primary-100); color: var(--primary); }

.hangup {
  padding: 10px;
  background: var(--danger);
  color: white;
  border: none;
  border-radius: var(--radius);
  font-weight: 600;
  font-size: 13px;
  display: flex; align-items: center; justify-content: center; gap: 6px;
  cursor: pointer;
  transition: background 0.1s;
}
.hangup:hover { background: #B91C1C; }

/* ── Volume micro ── */
.mic-vol {
  display: flex; align-items: center; gap: 8px;
  margin-top: 8px; padding: 7px 10px;
  border: 1px solid var(--border); border-radius: var(--radius);
  background: var(--surface);
}
.mic-vol-ico { color: var(--text-muted); flex-shrink: 0; }
.mic-range { flex: 1; min-width: 0; accent-color: var(--primary); cursor: pointer; height: 4px; }
.mic-range:disabled { cursor: not-allowed; opacity: 0.55; }
.idle-audio-hint { display: flex; align-items: center; gap: 5px; margin: 6px 0 0; font-size: 11px; line-height: 1.4; color: var(--text-muted); }
.mic-vol-pct {
  flex-shrink: 0; min-width: 38px; text-align: right;
  font-size: 11.5px; font-weight: 600; color: var(--text-secondary);
  font-variant-numeric: tabular-nums;
}

/* Filtre micro (noise gate) + vumètre de réglage */
/* Panneau réglages micro repliable (déclutter du panneau Téléphone) */
.mic-settings { margin-top: 8px; }
.mic-settings-toggle {
  display: flex; align-items: center; gap: 8px; width: 100%;
  padding: 7px 10px; cursor: pointer;
  border: 1px solid var(--border); border-radius: var(--radius);
  background: var(--surface); color: var(--text-secondary);
  font-size: 11.5px; font-weight: 600;
}
.mic-settings-toggle:hover { background: var(--surface-hover); }
.mic-settings-toggle .mic-st-label { flex-shrink: 0; }
.mic-settings-toggle .mic-st-summary {
  flex: 1; text-align: right; color: var(--text-muted);
  font-weight: 600; font-variant-numeric: tabular-nums;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}
.mic-settings-toggle .mic-st-chev {
  flex-shrink: 0; color: var(--text-muted);
  transition: transform var(--duration-fast) var(--ease-out);
}
.mic-settings-toggle.open .mic-st-chev { transform: rotate(180deg); }
/* Dans le corps replié, les blocs n'ont pas de marge supérieure superflue. */
.mic-settings-body .mic-gate:first-child { margin-top: 6px; }

.mic-gate {
  display: flex; flex-direction: column; gap: 7px;
  margin-top: 8px; padding: 9px 10px;
  border: 1px solid var(--border); border-radius: var(--radius);
  background: var(--surface);
}
.mic-gate-head { display: flex; align-items: center; gap: 7px; }
.mic-gate-title { flex: 1; font-size: 11.5px; font-weight: 600; color: var(--text-secondary); }
.mic-gate-state { flex-shrink: 0; font-size: 10.5px; font-weight: 700; color: var(--text-muted); }
.mic-gate-state.open { color: var(--success); }
.clarity-toggle {
  flex-shrink: 0; font-size: 10.5px; font-weight: 700;
  padding: 2px 8px; border-radius: 999px; cursor: pointer;
  color: var(--text-muted); background: var(--surface-2, rgba(127,127,127,.12));
  border: 1px solid var(--border, rgba(127,127,127,.25)); transition: all .15s;
}
.clarity-toggle.on {
  color: #fff; background: var(--accent, #2563eb); border-color: var(--accent, #2563eb);
}
.mic-gate-test {
  flex-shrink: 0; font-size: 11px; font-weight: 600; padding: 3px 10px;
  border: 1px solid var(--border); border-radius: var(--radius-full);
  background: var(--bg); color: var(--text-secondary); cursor: pointer;
}
.mic-gate-test:hover { background: var(--surface-hover); }
.mic-gate-test.active { background: var(--danger); color: #fff; border-color: transparent; }
.gate-meter {
  position: relative; height: 8px; overflow: hidden;
  border: 1px solid var(--border); border-radius: var(--radius-full);
  background: var(--bg);
}
.gate-meter-fill {
  position: absolute; left: 0; top: 0; bottom: 0; width: 0;
  background: var(--text-muted); transition: width 60ms linear;
}
.gate-meter-fill.open { background: var(--success); }
.gate-meter-thresh {
  position: absolute; top: 0; bottom: 0; width: 2px;
  background: var(--primary); transform: translateX(-1px);
}
.mic-gate-row { display: flex; align-items: center; gap: 8px; }
.mic-gate-cap { flex-shrink: 0; font-size: 10px; color: var(--text-muted); }
.mic-gate-hint { margin: 0; font-size: 10.5px; line-height: 1.35; color: var(--text-muted); }

.dtmf {
  margin-top: 6px; padding: 8px;
  border: 1px solid var(--border); border-radius: var(--radius);
  background: var(--surface); outline: none;
  transition: border-color var(--duration-base), box-shadow var(--duration-base);
}
.dtmf:focus, .dtmf:focus-within {
  border-color: var(--primary); box-shadow: 0 0 0 3px var(--primary-100);
}
.dtmf-readout {
  min-height: 26px; margin-bottom: 6px; padding: 4px 8px;
  display: flex; align-items: center;
  background: var(--bg); border: 1px solid var(--border); border-radius: var(--radius-sm);
  font-family: var(--font-mono); font-size: 15px; letter-spacing: 0.12em; color: var(--text);
  overflow: hidden;
}
.dtmf-sent { white-space: nowrap; overflow: hidden; text-overflow: ellipsis; direction: rtl; }
.dtmf-ph { font-family: inherit; font-size: 11.5px; letter-spacing: 0; color: var(--text-muted); }
.dtmf-grid {
  display: grid; grid-template-columns: repeat(3, 1fr); gap: 4px;
}
.dtmf-key {
  padding: 8px; background: var(--surface-hover); color: var(--text);
  border: 1px solid var(--border); border-radius: var(--radius-sm);
  font-weight: 600; font-size: 14px; cursor: pointer;
}
.dtmf-key:hover { background: var(--surface-active); }
.dtmf-key:active { background: var(--primary-100); border-color: var(--primary); }
.dtmf-hint {
  display: flex; align-items: center; gap: 5px;
  margin: 7px 0 0; font-size: 10.5px; color: var(--text-muted); line-height: 1.4;
}
.dtmf-hint b { color: var(--text-secondary); font-family: var(--font-mono); }

.incoming-row { display: grid; grid-template-columns: 1fr 1fr; gap: 8px; }
.answer {
  padding: 12px;
  background: #16A34A;
  color: white;
  border: none;
  border-radius: var(--radius);
  font-weight: 600;
  font-size: 13px;
  display: flex; align-items: center; justify-content: center; gap: 6px;
  cursor: pointer;
  transition: background 0.1s;
  animation: pulse-green 1.2s infinite;
}
.answer:hover { background: #15803D; }
@keyframes pulse-green {
  0%, 100% { box-shadow: 0 0 0 0 rgba(22, 163, 74, 0.6); }
  50% { box-shadow: 0 0 0 8px rgba(22, 163, 74, 0); }
}

.warn {
  display: flex; align-items: center; gap: 6px;
  padding: 8px 10px;
  background: var(--warning-bg);
  color: var(--warning);
  border: 1px solid var(--warning-border);
  border-radius: var(--radius);
  font-size: 12px;
}
.warn b { font-weight: 600; }

/* ─── Transcription ────────────────────────────────────────────────────── */
.tool-btn-transcription.active { border-color: #7C3AED; color: #7C3AED; background: #EDE9FE; }

.transcription-panel {
  border: 1px solid #DDD6FE;
  border-radius: var(--radius);
  background: #F5F3FF;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  max-height: 160px;
}
.transcription-header {
  display: flex; align-items: center; gap: 6px;
  padding: 6px 10px;
  background: #EDE9FE;
  border-bottom: 1px solid #DDD6FE;
  font-size: 11px; font-weight: 600; color: #5B21B6;
}
.transcription-dot {
  width: 7px; height: 7px; border-radius: 50%;
  background: #7C3AED;
  animation: pulse-purple 1.2s infinite;
  flex-shrink: 0;
}
@keyframes pulse-purple {
  0%, 100% { box-shadow: 0 0 0 0 rgba(124,58,237,0.5); }
  50%       { box-shadow: 0 0 0 5px rgba(124,58,237,0); }
}
.transcription-label { color: #5B21B6; }
.transcription-body {
  flex: 1; overflow-y: auto;
  padding: 8px 10px;
  display: flex; flex-direction: column; gap: 4px;
}
.transcription-empty { font-size: 11.5px; color: var(--text-muted); font-style: italic; margin: 0; }
.transcription-line {
  font-size: 12.5px; line-height: 1.45; color: var(--text);
  margin: 0;
  transition: color 0.15s;
}
.transcription-line.interim { color: var(--text-secondary); font-style: italic; }

.transcription-denied {
  display: flex; align-items: center; gap: 6px;
  padding: 7px 10px;
  background: var(--warning-bg);
  color: var(--warning);
  border: 1px solid var(--warning-border);
  border-radius: var(--radius);
  font-size: 12px;
}

/* ─── Bouton détacher / agrandir ──────────────────────────────────────── */
.compact-toggle {
  width: 24px; height: 24px;
  border-radius: var(--radius-sm);
  background: transparent;
  border: 1px solid var(--border);
  color: var(--text-muted);
  display: flex; align-items: center; justify-content: center;
  cursor: pointer;
  transition: background var(--duration-fast), color var(--duration-fast);
}
.compact-toggle:hover { background: var(--surface-hover); color: var(--text); }

/* ─── Mode compact (mini softphone plein écran) ───────────────────────── */
.panel-compact {
  width: 100% !important;
  min-width: 0 !important;
  border-left: none;
  padding: 12px;
  gap: 12px;
}
.panel-compact .head {
  gap: 6px;
  flex-wrap: wrap;
}
.compact-status {
  border: 1px solid var(--border);
  border-radius: var(--radius);
  padding: 8px 10px;
  background: var(--surface-hover);
}
.compact-status :deep(.status-selector),
.compact-status :deep(button) { width: 100%; }
</style>
